import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { observer } from 'mobx-react';
import moment from 'moment';

import { usePublicCompany } from 'client/hooks';
import RatingEmoji from 'components/RatingEmoji';
import { currentLanguage } from 'helpers/utils';
import LogoIcon from 'layouts/main-sidebar/LogoIcon';
import usePublicApi from 'public-api';
import { useAsyncFunction } from 'stores/hooks';
import { Card, Icon, Loader, Overlay, Segment } from 'ui';

type RouteParams = { companyId: UUIDField; lang: string };

function BadgeReport() {
  const publicApi = usePublicApi();
  const { companyId, lang } = useParams<RouteParams>() as { companyId: UUIDField; lang: string };
  const { company } = usePublicCompany();

  const [canRetainBadge, setCanRetainBadge] = useState<boolean | null>(null);
  const [comments, setComments] = useState<BackendPublic.RatingComment[] | null>(null);
  const [nextPage, setNextPage] = useState<number | null>(1);

  const load = async () => {
    if (!companyId || !lang || nextPage === null) return;
    const data = await publicApi.BadgeBadgeReportCommentsApi.list({
      company_id: companyId!,
      language: lang!,
      page: nextPage,
      page_size: 10,
    });

    setComments((prev) => [...(prev ?? []), ...data.results]);
    setNextPage(data.total_pages > nextPage ? nextPage + 1 : null);
  };

  const [handleLoad, isLoading] = useAsyncFunction(load);

  const intersecionObserver = useRef<IntersectionObserver | null>(null);
  const lastCommentElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (intersecionObserver.current) intersecionObserver.current.disconnect();
      intersecionObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && nextPage && !isLoading) {
          handleLoad();
        }
      });
      if (node) intersecionObserver.current.observe(node);
    },
    [isLoading, nextPage],
  );

  const loadIfCanRetainBadge = async () => {
    if (!companyId || !lang) return;
    const data = await publicApi.BadgeCanRetainBadgeApi.retrieve({
      company_id: companyId,
    });

    setCanRetainBadge(data.can_retain_badge);
  };

  useEffect(() => {
    const loadData = async () => {
      await loadIfCanRetainBadge();
      await handleLoad();
    };
    loadData();
  }, []);

  if (company === null || (comments === null && isLoading)) {
    return (
      <Overlay open onOverlayClick={() => null}>
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
          <Loader size="huge" />
        </div>
      </Overlay>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto max-w-4xl md:p-6">
        <div className="space-y-6 text-center">
          <div className="px-4 py-8 shadow sm:px-10 md:rounded-md">
            <div className="flex flex-col items-center justify-around md:flex-row">
              {company.logo_url ? (
                <img src={currentLanguage(company.logo_url)} alt={currentLanguage(company.name)} width={250} />
              ) : (
                <div
                  style={{
                    fontSize: 40,
                    fontWeight: 700,
                    width: 250,
                    textAlign: 'center',
                  }}
                >
                  {currentLanguage(company.name)}
                </div>
              )}
              {canRetainBadge && (
                <img
                  src={`https://cdn.retino.com/media/${companyId}/badges/${lang}/current/icon.png`}
                  width={250}
                  alt={gettext('Effortless returns')}
                />
              )}
            </div>
          </div>
          {canRetainBadge !== null && (
            <Segment padded>
              {canRetainBadge ? (
                <>
                  <h4 className="text-2xl text-green-600">
                    <Icon name="badge-check" size="huge" />
                    {gettext('This merchant has received the Effortless returns certificate')}
                  </h4>
                  <p className="mx-auto max-w-xl pt-4 text-gray-600">
                    {gettext(
                      "No need to worry to return goods with this merchant. This merchant's customers have been consistently rating the return experience as great here.",
                    )}
                  </p>
                </>
              ) : (
                <h4 className="text-2xl text-red-600">
                  <Icon name="ban" size="huge" />
                  {gettext(
                    'This merchant does not meet the requirements to receive the Effortless returns certificate',
                  )}
                </h4>
              )}
            </Segment>
          )}
          {comments && comments.length > 0 && (
            <p className="pt-6 pb-4 text-center text-gray-500">
              {gettext('Customers that have recently returned something to this store say:')}
            </p>
          )}
        </div>
        {comments && comments.length > 0 && (
          <Card>
            {comments.map((item, index) => (
              <div
                key={`${item.customer_rating_comment} - ${item.created_at}`}
                className={`p-5 ${index === 0 ? '' : 'border-t border-gray-300'}`}
                ref={index === comments.length - 1 ? lastCommentElementRef : null}
              >
                <div className="flex items-baseline justify-between">
                  <div className="flex items-center mb-2">
                    <strong className="pr-2">{gettext('Customer has rated')}</strong>
                    <RatingEmoji rating={item.customer_rating as 0 | 1 | 2} size="small" />
                  </div>
                  <p className="text-gray-600">{moment(item.created_at).format('LL')}</p>
                </div>
                <p>{item.customer_rating_comment}</p>
                {item.customer_rating_comment_response && (
                  <div className="mt-4 p-4 bg-gray-100 border border-gray-300 rounded-lg shadow">
                    <div className="flex items-center mb-2">
                      <Icon name="reply" className="text-gray-700 w-5 h-5 mr-2" />
                      <p className="font-semibold text-gray-800">{currentLanguage(company.name)}</p>
                    </div>
                    <p className="text-gray-800 break-words">{item.customer_rating_comment_response}</p>
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="align-center p-8 w-full text-center flex justify-center items-center border-t border-gray-300">
                <Loader size="medium" />
              </div>
            )}
          </Card>
        )}

        <div className="mx-auto max-w-lg space-y-3 py-12 text-center">
          <p className="text-gray-500">
            {gettext(
              'Effortless returns certificate is given to merchant that is rated 90%+ by their customers in return experience over the past 3 months.',
            )}
          </p>
          <p>
            <a href="https://www.bezproblemovereklamace.cz/" className="text-blue-700 underline">
              {gettext('More about the certificate')}
            </a>
          </p>
          <div className="mx-auto w-20 pt-2 text-gray-400">
            <LogoIcon />
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(BadgeReport);
